import axios from 'axios';
import {
  FetchChartDataParams,
  ChartDataResponse,
} from '@/modules/calendar/model/data/data-impact-chart';

async function fetchChartDataPage({
  eventTimestampMs,
  session,
  symbols,
  bucketsPerPage = 300,
  page = 1,
  symbolsLimit = 5,
}: FetchChartDataParams & { page?: number }): Promise<{ data: ChartDataResponse; status: number }> {
  const params = [
    `session=${session || ''}`,
    'type=chart',
    `symbols=${symbols}`,
    `timestamp=${eventTimestampMs}`,
    `bucketsPerPage=${bucketsPerPage}`,
    `timePage=${page}`,
    `symbolsLimit=${symbolsLimit}`,
  ].join('&');
  return axios
    .get(`/api/miss?${params}`)
    .then(value => ({ data: value.data, status: value.status }));
}

async function fetchChartData(
  {
    eventTimestampMs,
    session,
    symbols,
    bucketsPerPage = 300,
    symbolsLimit = 5,
  }: FetchChartDataParams,
  signal: AbortSignal,
  maxAttempts = 12,
  pollInterval = 15000,
): Promise<ChartDataResponse | null> {
  // Accumulate data across pages
  let allData: ChartDataResponse | null = null;
  let currentPage = 1;
  let totalPages = 1;

  while (currentPage <= totalPages) {
    if (signal.aborted) throw new Error('Fetch cancelled');

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (signal.aborted) throw new Error('Fetch cancelled');

      try {
        const resp = await fetchChartDataPage({
          eventTimestampMs,
          session,
          symbols,
          bucketsPerPage,
          page: currentPage,
          symbolsLimit,
        });

        const status = resp && typeof resp === 'object' && 'status' in resp ? resp.status : 200;
        const json = resp && typeof resp === 'object' && 'data' in resp ? resp.data : resp;

        if (status === 202) {
          if (attempt < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));
            continue;
          } else {
            throw new Error('Timeout: Chart data took too long to process');
          }
        }

        if (status >= 400 && status < 600) {
          let apiErrorText = '';
          let errorField = '';
          try {
            apiErrorText = typeof json === 'string' ? json : JSON.stringify(json);
            if (json && typeof json === 'object' && 'error' in json)
              errorField = String(json.error);
          } catch {}
          if (errorField) throw new Error('Error while fetching chart data: ' + errorField);
          throw new Error(
            `Error while fetching chart data: API error ${status}: ${String(apiErrorText)}`,
          );
        }

        // Extract totalPages from response
        if (json && typeof json === 'object' && 'totalPages' in json && json.totalPages) {
          totalPages = json.totalPages as number;
        }

        // Merge data: concatenate prices per symbol
        if (!allData) {
          allData = json;
        } else if (json && json.data && Array.isArray(json.data)) {
          // Merge prices for each symbol across pages
          if (allData.data && Array.isArray(allData.data)) {
            for (let i = 0; i < json.data.length; i++) {
              if (i < allData.data.length) {
                const symbol = json.data[i]?.symbol || `symbol_${i}`;
                const prevPrices = Array.isArray(allData.data[i]?.prices)
                  ? allData.data[i].prices
                  : [];
                const newPrices = Array.isArray(json.data[i]?.prices) ? json.data[i].prices : [];
                const prevLength = prevPrices.length;

                // Merge prices array
                allData.data[i].prices = prevPrices.concat(newPrices);
                const newLength = allData.data[i].prices.length;

                console.log(
                  `[fetchChartData] Page ${currentPage} - ${symbol}: merged ${prevLength} + ${newLength - prevLength} prices = ${newLength} total`,
                );
              }
            }
          }
        }

        currentPage++;
        break; // Success, move to next page
      } catch (err: any) {
        if (err?.message === 'Fetch cancelled' || signal.aborted) throw err;
        if (attempt === maxAttempts) {
          console.error(
            `[fetchChartData] Page ${currentPage} failed after ${maxAttempts} attempts`,
            err,
          );
          throw err;
        }

        console.warn(
          `[fetchChartData] Page ${currentPage} attempt ${attempt} failed, retrying...`,
          err.message,
        );

        // If we get here and there's an axios-style response, surface it
        if (err.response && err.response.status >= 400 && err.response.status < 600) {
          let apiErrorText = '';
          let errorField = '';
          try {
            const data = err.response.data;
            apiErrorText = typeof data === 'string' ? data : JSON.stringify(data);
            if (data && typeof data === 'object' && 'error' in data)
              errorField = String(data.error);
          } catch {}
          if (errorField) throw new Error('Error while fetching chart data: ' + errorField);
          throw new Error(
            `Error while fetching chart data: API error ${err.response.status}: ${err.response.statusText}\n${apiErrorText}`,
          );
        }
      }
    }
  }

  return allData;
}

const impactApi = {
  fetchChartData,
};

export default impactApi;
